import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';

import {
  type MentionItem,
  MentionSuggestion,
  type MentionSuggestionKeyDownHandler,
  type MentionSuggestionRef,
} from './MentionSuggestion';

const MentionWithMarkdown = Mention.extend({
  addStorage() {
    return {
      markdown: {
        serialize(
          state: { write: (text: string) => void },
          node: { attrs: { label?: string; id: string } },
        ) {
          state.write(
            `@[${node.attrs.label ?? node.attrs.id}](${node.attrs.id})`,
          );
        },
        parse: {},
      },
    };
  },
});

export const createMentionExtension = (
  getSuggestions: (query: string) => MentionItem[],
  onOpenChange?: (open: boolean) => void,
  onMentionSearch?: (query: string) => void,
  _getIsLoading?: () => boolean,
  onRegisterUpdate?: (update: () => void) => void,
) =>
  MentionWithMarkdown.configure({
    HTMLAttributes: {
      class: 'mention',
    },
    renderText: ({ node }) =>
      `@[${node.attrs.label ?? node.attrs.id}](${node.attrs.id})`,
    renderHTML: ({ node, options, suggestion }) => [
      'span',
      options.HTMLAttributes,
      [
        'span',
        { class: 'mentionLabel' },
        `${suggestion?.char ?? '@'}${node.attrs.label ?? node.attrs.id}`,
      ],
    ],
    suggestion: {
      char: '@',
      allowSpaces: true,
      items: ({ query }) => getSuggestions(query),
      command: ({ editor, range, props }) => {
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const to = nodeAfter?.text?.startsWith(' ') ? range.to + 1 : range.to;
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to }, [
            {
              type: 'mention',
              attrs: { ...props, mentionSuggestionChar: '@' },
            },
            { type: 'text', text: ' ' },
          ])
          .setTextSelection(range.from + 2)
          .run();
      },
      render: () => {
        let component: ReactRenderer<MentionSuggestionRef> | null = null;
        let popup: HTMLDivElement | null = null;
        const keyDownRef: {
          current: MentionSuggestionKeyDownHandler | null;
        } = { current: null };

        const positionPopup = (
          clientRect: (() => DOMRect | null) | null | undefined,
        ) => {
          if (!popup || !clientRect) return;
          const rect = clientRect();
          if (!rect) return;
          const viewportWidth =
            document.documentElement.clientWidth || window.innerWidth;
          const popupWidth = popup.getBoundingClientRect().width;
          popup.style.left = `${Math.max(0, Math.min(rect.left, viewportWidth - popupWidth))}px`;
          popup.style.bottom = `${window.innerHeight - rect.top + 4}px`;
          popup.style.top = 'auto';
        };

        return {
          onStart: props => {
            onOpenChange?.(true);
            onMentionSearch?.(props.query);

            component = new ReactRenderer(MentionSuggestion, {
              props: { ...props, keyDownRef },
              editor: props.editor,
            });

            popup = document.createElement('div');
            popup.style.position = 'fixed';
            popup.style.zIndex = '99999';
            popup.appendChild(component.element);
            document.body.appendChild(popup);

            positionPopup(props.clientRect);

            onRegisterUpdate?.(() => {
              component?.updateProps({
                items: getSuggestions(''),
              });
            });
          },

          onUpdate: props => {
            onMentionSearch?.(props.query);
            component?.updateProps(props);
            positionPopup(props.clientRect);
          },

          onKeyDown: props => {
            if (props.event.key === 'Escape') {
              popup?.remove();
              onOpenChange?.(false);
              return true;
            }
            return (
              keyDownRef.current?.(props) ??
              component?.ref?.onKeyDown(props) ??
              false
            );
          },

          onExit: () => {
            keyDownRef.current = null;
            popup?.remove();
            component?.destroy();
            onOpenChange?.(false);
            onMentionSearch?.('');
          },
        };
      },
    },
  });
